<script lang="ts">
    import { getCachedEntries } from "../../api"
    import ProductPreview from "../ecommerce/product/preview/ProductPreview.svelte"
    import BarLoader from "svelte-loading-spinners/BarLoader.svelte"
    import {
        buildProductSearchFilter,
        escapeRegExp,
        getSearchWordsByPattern,
        scoreProductSearchMatch,
    } from "../../functionTools/search.js"
    import { initialLocale, location } from "../../i18n"
    import { _ } from "svelte-i18n"
    import Index from "../SEO/Index.svelte"

    if (typeof window === "undefined") {
        //@ts-ignore
        if (context) context.is404 = true
    }

    const localeSegments = new Set(["de", "en", "fr", "se", "dk"])
    const ignoredKeywordSegments = new Set(["product", "produkte", "preview"])
    const suggestionRegexFields = ["code", "name.de", "slug.de"]
    const suggestionRelevanceMap: Record<string, number> = {
        code: 1,
        "name.de": 0.8,
        "description.de": 0.6,
        "slug.de": 1.2,
    }
    const suggestionCandidateLimit = 9999
    const suggestionDisplayLimit = 10
    let suggestedProducts: Product[] = $state([])
    let loadingSuggestions = $state(false)

    function decodeSegment(segment: string) {
        try {
            return decodeURIComponent(segment)
        } catch {
            return segment
        }
    }

    function readWords(input: string) {
        return getSearchWordsByPattern(input, /[\s/_-]+/)
    }

    function getBaseArticleNumber(articleNumber: string) {
        if (!/^\d+-\d+$/.test(articleNumber)) return ""
        return articleNumber.split("-")[0] || ""
    }

    function getSuggestionInput(path: string, search: string) {
        const pathSegments = path
            .split("/")
            .filter(Boolean)
            .map(decodeSegment)
            .filter((segment) => !localeSegments.has(segment.toLowerCase()))
        const articleSegment = [...pathSegments].reverse().find((segment) => /^[\d-]+(?:_.+)?$/.test(segment)) || ""
        const articleNumber = articleSegment.split("_")[0] || ""
        const baseArticleNumber = getBaseArticleNumber(articleNumber)
        const articleSlug = articleSegment.includes("_") ? articleSegment.split("_").slice(1).join("_") : ""
        const searchValue = search.startsWith("?") ? search.substring(1) : search
        const pathKeywordSegments = pathSegments.filter((segment) => !ignoredKeywordSegments.has(segment.toLowerCase()))
        const keywordCandidates = [articleSlug, pathKeywordSegments.join(" "), decodeSegment(searchValue)]
        const keywords = Array.from(new Set(keywordCandidates.flatMap(readWords))).filter(
            (word) => word !== articleNumber && word !== baseArticleNumber
        )

        return {
            articleNumber,
            baseArticleNumber,
            keywords,
        }
    }

    function buildSuggestionFilter(articleNumber: string, baseArticleNumber: string, keywords: string[]) {
        const uniqueKeywords = keywords.slice(0, 6)
        const textSearch = [articleNumber, baseArticleNumber, ...uniqueKeywords].filter(Boolean).join(" ")
        const baseFilter = buildProductSearchFilter(textSearch, initialLocale, uniqueKeywords, suggestionRegexFields)
        const or = baseFilter?.$or || []

        if (articleNumber) {
            or.push({ code: articleNumber })
            or.push({
                code: {
                    $regex: `^${escapeRegExp(articleNumber)}`,
                    $options: "i",
                },
            })
        }

        if (baseArticleNumber) {
            or.push({ code: baseArticleNumber })
            or.push({ base_code: baseArticleNumber })
            or.push({
                code: {
                    $regex: `^${escapeRegExp(baseArticleNumber)}`,
                    $options: "i",
                },
            })
        }

        return or.length ? { $or: or } : null
    }

    function rankProduct(product: Product, articleNumber: string, baseArticleNumber: string, keywords: string[]) {
        let score = scoreProductSearchMatch(product, keywords, {
            articleNumber,
            relevanceMap: suggestionRelevanceMap,
        })

        if (baseArticleNumber) {
            if (product.code === baseArticleNumber) score += 1500
            else if (product.code?.startsWith(baseArticleNumber)) score += 700
            if (product.base_code === baseArticleNumber) score += 1200
        }

        return score
    }

    $effect(() => {
        const suggestionInput = getSuggestionInput($location.path || "/", $location.search || "")
        const filter = buildSuggestionFilter(
            suggestionInput.articleNumber,
            suggestionInput.baseArticleNumber,
            suggestionInput.keywords
        )

        if (!filter) {
            suggestedProducts = []
            loadingSuggestions = false
            return
        }

        loadingSuggestions = true
        getCachedEntries("product", filter, "sort", suggestionCandidateLimit)
            .then((products) => {
                suggestedProducts = [...products]
                    .sort(
                        (a, b) =>
                            rankProduct(
                                b,
                                suggestionInput.articleNumber,
                                suggestionInput.baseArticleNumber,
                                suggestionInput.keywords
                            ) -
                            rankProduct(
                                a,
                                suggestionInput.articleNumber,
                                suggestionInput.baseArticleNumber,
                                suggestionInput.keywords
                            )
                    )
                    .slice(0, suggestionDisplayLimit)
                loadingSuggestions = false
            })
            .catch(() => {
                suggestedProducts = []
                loadingSuggestions = false
            })
    })
</script>

<Index noIndex={true} title="404 – Seite nicht gefunden" metaDescription="Diese Seite wurde nicht gefunden" />
<section class="section_padding">
    <div class="container-fluid">
        <h1>Seite nicht gefunden</h1>
        <p>Die gesuchte Seite ist nicht verfügbar. Vielleicht helfen Ihnen diese passenden Produkte weiter.</p>
        {#if loadingSuggestions}
            <div class="suggestions-loader">
                <BarLoader />
            </div>
        {:else if suggestedProducts.length}
            <section class="suggestions">
                <h2>Das könnte Sie interessieren</h2>
                <div class="products-inner-wrapper">
                    <ul>
                        {#each suggestedProducts as product (product.id)}
                            <ProductPreview {product} />
                        {/each}
                    </ul>
                </div>
            </section>
        {/if}
    </div>
</section>

<style lang="less">
    .suggestions {
        margin-top: 4rem;

        h2 {
            margin-bottom: 1.6rem;
        }

        .products-inner-wrapper {
            width: 100%;

            ul {
                display: grid;
                grid-template-columns: repeat(5, minmax(0, 1fr));
                gap: 2.4rem;
                padding: 0;
                margin: 0;
                list-style: none;
            }
        }
    }

    .suggestions-loader {
        margin-top: 4rem;
    }

    @media (max-width: 1200px) {
        .suggestions .products-inner-wrapper ul {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }
    }

    @media (max-width: 900px) {
        .suggestions .products-inner-wrapper ul {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }

    @media (max-width: 640px) {
        .suggestions .products-inner-wrapper ul {
            grid-template-columns: 1fr;
        }
    }
</style>
